<script lang="ts">
	import { fly, fade } from 'svelte/transition';
	import { toastStore } from '../stores/toastStore';
	import type { ToastItem } from '../stores/toastStore';
	import { Icon } from "flowbite-svelte-icons";

	export let item: ToastItem;

	const icons: Record<string, string> = {
		success: 'check-circle-solid',
		error: 'close-circle-solid',
		warning: 'exclamation-circle-solid',
		info: 'info-circle-solid',
	};

	const glows: Record<string, string> = {
		success: '--glow: 16, 185, 129;',
		error: '--glow: 239, 68, 68;',
		warning: '--glow: 245, 158, 11;',
		info: '--glow: 56, 189, 248;',
	};
</script>

<div
	class="glass-toast glass-toast--{item.type}"
	style={glows[item.type]}
	in:fly={{ y: -28, duration: 280, opacity: 0 }}
	out:fade={{ duration: 180 }}
	role="alert"
>
	<div class="glass-toast__icon">
		<Icon name={icons[item.type]} class="w-4 h-4" />
	</div>
	<div class="glass-toast__content">
		<span class="glass-toast__message">{item.message}</span>
		{#if item.detail}
			<span class="glass-toast__detail">{item.detail}</span>
		{/if}
	</div>
	<button
		class="glass-toast__dismiss"
		on:click={() => toastStore.dismiss(item.id)}
		aria-label="Dismiss"
	>
		<Icon name="close-solid" class="w-3 h-3" />
	</button>
	{#if item.progress != null}
		<div class="glass-toast__progress">
			<div class="glass-toast__progress-bar" style="width: {Math.round(item.progress * 100)}%"></div>
		</div>
	{/if}
</div>

<style>
	.glass-toast {
		display: flex;
		/* Top-aligned, now that the message can be several lines: centring put the icon and the
		   dismiss button halfway down a paragraph, level with nothing. */
		align-items: flex-start;
		gap: 12px;
		padding: 12px 18px;
		min-width: 280px;
		max-width: 420px;
		border-radius: 18px;
		pointer-events: auto;
		position: relative;
		overflow: hidden;
		background: rgba(28, 28, 30, 0.55);
		backdrop-filter: blur(40px) saturate(180%);
		-webkit-backdrop-filter: blur(40px) saturate(180%);
		border: 1px solid rgba(255, 255, 255, 0.08);
		box-shadow:
			0 8px 32px rgba(0, 0, 0, 0.35),
			inset 0 1px 0 rgba(255, 255, 255, 0.06),
			0 0 0 0.5px rgba(255, 255, 255, 0.04);
	}

	.glass-toast::before {
		content: '';
		position: absolute;
		inset: -1px;
		border-radius: 18px;
		padding: 1px;
		background: linear-gradient(
			135deg,
			rgba(var(--glow), 0.3) 0%,
			rgba(var(--glow), 0.05) 50%,
			transparent 100%
		);
		-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
		mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
	}

	.glass-toast::after {
		content: '';
		position: absolute;
		top: 0;
		left: 10%;
		right: 10%;
		height: 1px;
		background: linear-gradient(
			90deg,
			transparent,
			rgba(255, 255, 255, 0.15),
			transparent
		);
		pointer-events: none;
	}

	.glass-toast__icon {
		flex-shrink: 0;
		filter: drop-shadow(0 0 6px rgba(var(--glow), 0.5));
	}

	.glass-toast--success .glass-toast__icon { color: rgb(52, 211, 153); }
	.glass-toast--error .glass-toast__icon { color: rgb(248, 113, 113); }
	.glass-toast--warning .glass-toast__icon { color: rgb(251, 191, 36); }
	.glass-toast--info .glass-toast__icon { color: rgb(125, 211, 252); }

	.glass-toast__content {
		display: flex;
		flex-direction: column;
		min-width: 0;
		flex: 1;
	}

	/*
	 * Toasts wrap. They used to be one line each, clipped with an ellipsis — and what a toast
	 * carries here is the *server's own sentence*: `RequestError` messages go straight to
	 * `toastError`, and the whole reason they travel is that only the server knows which refusal
	 * this was. "That price cannot be…" is not that sentence; it is the first forty characters of
	 * it, and the rest is unrecoverable because the toast dismisses itself.
	 *
	 * Clamped rather than unbounded: a stack trace in a corner overlay would cover the screen it is
	 * reporting on. The cap is generous enough for any refusal the API writes, and the ellipsis now
	 * means "there is more of this" instead of "there was more of this on line one".
	 */
	.glass-toast__message {
		font-size: 13px;
		font-weight: 500;
		color: rgba(255, 255, 255, 0.92);
		line-height: 1.35;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 5;
		line-clamp: 5;
		overflow: hidden;
	}

	.glass-toast__detail {
		font-size: 11px;
		color: rgba(255, 255, 255, 0.5);
		line-height: 1.35;
		margin-top: 2px;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 3;
		line-clamp: 3;
		overflow: hidden;
	}

	.glass-toast__dismiss {
		flex-shrink: 0;
		width: 20px;
		height: 20px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		color: rgba(255, 255, 255, 0.35);
		background: rgba(255, 255, 255, 0.06);
		border: none;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.glass-toast__dismiss:hover {
		color: rgba(255, 255, 255, 0.8);
		background: rgba(255, 255, 255, 0.12);
	}

	.glass-toast__progress {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 3px;
		background: rgba(255, 255, 255, 0.06);
		border-radius: 0 0 18px 18px;
		overflow: hidden;
	}

	.glass-toast__progress-bar {
		height: 100%;
		background: rgba(var(--glow), 0.7);
		transition: width 0.2s ease;
		border-radius: 0 0 18px 18px;
	}
</style>
